// Shared swipeable, sortable player ratings table used by both the Roster (My Team) and Free
// Agents screens so they stay identical. Sticky player-name column (no avatar) + horizontal
// scroll of Contract (Salary / Length) → Physical → Shooting → Ratings columns, with an optional
// sticky-right per-row action (Trade on the roster, Offer on free agents). Tap a column to sort;
// tap a name to open that player.

import { useState, type ReactNode } from "react";
import { SKILLS, orderSkills } from "../../common/constants.ts";

const TEAL = "#18E0A8";

// Most players carry ≤4 skill badges; cap the row so heights stay uniform and, thanks to the
// priority order in orderSkills, the ones that survive are the important ones (Di/Dp/3/R/V/B).
const MAX_ROW_SKILLS = 4;
const ratingColor = (v: number) =>
	v >= 80 ? "#18E0A8" : v >= 62 ? "#4fd6a8" : v >= 48 ? "#E8B923" : "#8f9aa2";

type Col = { key: string; label: string; contract?: boolean };
const GROUPS: { label: string; cols: Col[] }[] = [
	{
		label: "OVERALL",
		cols: [
			{ key: "ovr", label: "OVR" },
			{ key: "pot", label: "POT" },
		],
	},
	{
		label: "CONTRACT",
		cols: [
			{ key: "sal", label: "SAL", contract: true },
			{ key: "len", label: "LEN", contract: true },
			{ key: "age", label: "AGE", contract: true },
		],
	},
	{
		label: "PHYSICAL",
		cols: [
			{ key: "hgt", label: "HGT" },
			{ key: "stre", label: "STR" },
			{ key: "spd", label: "SPD" },
			{ key: "jmp", label: "JMP" },
			{ key: "endu", label: "STA" },
		],
	},
	{
		label: "SHOOTING",
		cols: [
			{ key: "reb", label: "REB" },
			{ key: "ins", label: "INS" },
			{ key: "dnk", label: "LAY" },
			{ key: "ft", label: "FT" },
			{ key: "fg", label: "MID" },
			{ key: "tp", label: "3PT" },
		],
	},
	{
		label: "IQ",
		cols: [
			{ key: "oiq", label: "IQo" },
			{ key: "diq", label: "IQd" },
		],
	},
];
const ALL_COLS: Col[] = GROUPS.flatMap((g) => g.cols);

const PLAYER_W = 116;
const CELL_W = 40;
const ACT_W = 78;

export const PlayerTable = ({
	players,
	season,
	onName,
	action,
	defaultSort = "ovr",
}: {
	players: any[];
	// Current season, so contract length = contract.exp - season.
	season: number;
	onName: (p: any) => void;
	// Optional per-row action (Trade / Offer). Omit for a read-only table.
	action?: (p: any) => ReactNode;
	defaultSort?: string;
}) => {
	const [sortKey, setSortKey] = useState(defaultSort);
	const [dir, setDir] = useState(-1); // -1 desc, 1 asc
	const setSort = (key: string) => {
		if (sortKey === key) {
			setDir((d) => -d);
		} else {
			setSortKey(key);
			setDir(-1);
		}
	};

	const valOf = (p: any, key: string): number => {
		if (key === "sal") return p.contract?.amount ?? 0;
		if (key === "len") return Math.max(0, (p.contract?.exp ?? season) - season);
		if (key === "age") return p.age ?? 0;
		return p.ratings?.[key] ?? 0;
	};

	const rows = [...players].sort(
		(a, b) => (valOf(a, sortKey) - valOf(b, sortKey)) * dir,
	);

	const hasAct = !!action;
	const tableWidth = PLAYER_W + ALL_COLS.length * CELL_W + (hasAct ? ACT_W : 0);

	const cellText = (p: any, c: Col) => {
		if (c.key === "sal") return `$${Math.round(valOf(p, "sal"))}`;
		if (c.key === "len") {
			const y = valOf(p, "len");
			return y > 0 ? `${y}y` : "—";
		}
		return `${Math.round(valOf(p, c.key))}`;
	};

	return (
		<div className="pt-wrap">
			<style>{styles}</style>
			<div className="pt-hint">SWIPE THE TABLE → · TAP A COLUMN TO SORT</div>
			<div className="pt-scroll">
				<div className="pt-table" style={{ minWidth: tableWidth }}>
					{/* group header row */}
					<div className="pt-row pt-grouprow">
						<div className="pt-sticky-l pt-gp-spacer" />
						{GROUPS.map((g) => (
							<div
								className="pt-gp"
								key={g.label}
								style={{ width: g.cols.length * CELL_W }}
							>
								{g.label}
							</div>
						))}
						{hasAct ? <div className="pt-sticky-r pt-gp-spacer" /> : null}
					</div>

					{/* column header row */}
					<div className="pt-row pt-headrow">
						<div className="pt-sticky-l pt-th-player">PLAYER</div>
						<div className="pt-cells">
							{ALL_COLS.map((c) => {
								const active = sortKey === c.key;
								return (
									<div
										className="pt-th"
										key={c.key}
										style={{ width: CELL_W, color: active ? TEAL : "#7f8b93" }}
										onClick={() => setSort(c.key)}
									>
										{c.label}
										{active ? (dir < 0 ? " ↓" : " ↑") : ""}
									</div>
								);
							})}
						</div>
						{hasAct ? <div className="pt-sticky-r pt-th-act" /> : null}
					</div>

					{/* player rows */}
					{rows.map((p) => (
						<div className="pt-row pt-datarow" key={p.pid}>
							<div
								className="pt-sticky-l pt-td-player"
								onClick={() => onName(p)}
							>
								<div className="pt-pn-name">
									{p.firstNameShort ?? p.firstName} {p.lastName}
								</div>
								<div className="pt-pn-meta">{p.ratings.pos}</div>
								{(() => {
									const sk = orderSkills(p.ratings?.skills ?? []);
									if (sk.length === 0) return null;
									const shown = sk.slice(0, MAX_ROW_SKILLS);
									const extra = sk.length - shown.length;
									return (
										<div className="pt-pn-skills">
											{shown.map((k) => (
												<span className="pt-skill" key={k} title={SKILLS[k] ?? k}>
													{k}
												</span>
											))}
											{extra > 0 ? (
												<span
													className="pt-skill pt-skill-more"
													title={sk.slice(MAX_ROW_SKILLS).join(" ")}
												>
													+{extra}
												</span>
											) : null}
										</div>
									);
								})()}
							</div>
							<div className="pt-cells">
								{ALL_COLS.map((c) => {
									const color = c.contract
										? "#cfe0ec"
										: ratingColor(valOf(p, c.key));
									return (
										<span
											className="pt-cell"
											key={c.key}
											style={{ width: CELL_W, color }}
										>
											{cellText(p, c)}
										</span>
									);
								})}
							</div>
							{hasAct ? (
								<div className="pt-sticky-r pt-td-act">{action!(p)}</div>
							) : null}
						</div>
					))}
				</div>
			</div>
		</div>
	);
};

const styles = `
.pt-hint { font-size:10.5px; font-weight:800; letter-spacing:.4px; color:#6f7b83; margin:6px 0 10px; }
.pt-scroll { overflow-x:auto; -ms-overflow-style:none; scrollbar-width:none; }
.pt-scroll::-webkit-scrollbar { width:0; height:0; }
.pt-row { display:flex; align-items:stretch; }
.pt-datarow { border-top:1px solid #202020; }
.pt-datarow:hover { background:#161616; }
.pt-sticky-l { position:sticky; left:0; z-index:2; width:${PLAYER_W}px; flex:none; background:#121212; }
.pt-sticky-r { position:sticky; right:0; z-index:2; width:${ACT_W}px; flex:none; background:#121212;
  display:flex; align-items:center; justify-content:center; }
.pt-cells { display:flex; flex:none; }
.pt-grouprow { padding-bottom:4px; }
.pt-gp { flex:none; text-align:center; font-size:8.5px; font-weight:900; letter-spacing:.6px;
  color:#6f7b83; border-bottom:1px solid #2a2a2a; padding-bottom:4px; }
.pt-gp-spacer { background:#121212; }
.pt-headrow { padding:6px 0 8px; border-bottom:1px solid #2a2a2a; }
.pt-th-player { display:flex; align-items:flex-end; font-size:8.5px; font-weight:800; color:#6f7b83;
  letter-spacing:.5px; padding-left:2px; }
.pt-th { flex:none; text-align:center; font-size:9px; font-weight:800; letter-spacing:.3px;
  cursor:pointer; align-self:flex-end; }
.pt-td-player { display:flex; flex-direction:column; justify-content:center; padding:10px 8px 10px 2px; cursor:pointer; }
.pt-pn-name { font-size:13px; font-weight:800; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.pt-pn-meta { font-size:10px; font-weight:600; color:#7f8b93; margin-top:1px; }
.pt-pn-skills { display:flex; gap:3px; margin-top:4px; }
.pt-skill { height:15px; padding:0 4px; border-radius:4px; background:#17251f; color:#18E0A8;
  font-size:8.5px; font-weight:900; letter-spacing:.2px; display:flex; align-items:center; }
.pt-skill-more { background:#1b1b1b; color:#7f8b93; }
.pt-cell { flex:none; text-align:center; font-size:12px; font-weight:700; align-self:center;
  display:flex; align-items:center; justify-content:center; }
.pt-actbtn { background:none; border:1px solid #2f6b52; color:#18E0A8; font-family:inherit;
  font-size:11px; font-weight:800; padding:6px 12px; border-radius:14px; cursor:pointer; min-width:48px; }
.pt-actbtn:hover { background:rgba(24,224,168,.12); }
.pt-actbtn.on { background:#18E0A8; color:#053a2c; border-color:#18E0A8; }
.pt-actbtn:disabled { opacity:.35; cursor:default; }
`;

export default PlayerTable;
